import { useRef, useState, DragEvent, useCallback } from 'react';
import classNames from 'classnames';

import styles from './sortable-item.module.scss';

const inputFieldSelector = ['textarea:focus', 'input:focus', 'select:focus'].join(',');

interface SortableItemProps {
  index: number;
  scope: string;
  draggedIndex: number | null;
  enabled?: boolean;
  setDraggedIndex: (index: number | null) => void;
  onReorder: (fromIndex: number, toIndex: number, after: boolean) => void;
  shouldDropAfter?: (event: DragEvent, index: number, draggedIndex: number) => boolean;
  children: React.ReactNode;
  className?: string;
  classNames?: {
    dragBefore: string;
    dragAfter: string;
  };
}

export const SortableItem = (props: SortableItemProps) => {
  const { index, draggedIndex, enabled = true } = props;
  const [isDragOver, setIsDragOver] = useState(false);
  const [isDragAfter, setIsDragAfter] = useState<boolean | null>(null);
  const element = useRef<HTMLDivElement>(null);

  // Allow dragging within inputs to work as expected
  const [isInputFieldFocused, setInputFieldFocused] = useState<boolean>(false);

  const onBlur = () => {
    setInputFieldFocused(false);
  };

  const onFocus = () => {
    setInputFieldFocused(element.current?.querySelector(inputFieldSelector) !== null);
  };

  // Defines different "sortable groups" and allows us to check for it in dragOver and dragEnter events
  const dataKey = `application/x-sup-sort-${props.scope.toLowerCase()}`;

  const isDragged = draggedIndex === index;

  return (
    <div
      className={classNames(props.className, {
        [styles.sortable]: enabled,
        [styles.isDragged]: isDragged,
        [props.classNames?.dragBefore ?? '']: isDragOver && !isDragged && isDragAfter === false,
        [props.classNames?.dragAfter ?? '']: isDragOver && !isDragged && isDragAfter === true,
      })}
      ref={element}
      onBlur={onBlur}
      onFocus={onFocus}
      draggable={enabled && !isInputFieldFocused}
      onDragStart={(event) => {
        if (enabled) {
          event.stopPropagation(); // Allow nested sortables
          event.dataTransfer.clearData();
          event.dataTransfer.setData(dataKey, index.toString());
          event.dataTransfer.effectAllowed = 'move';
          props.setDraggedIndex(index);
        }
      }}
      onDragEnd={() => {
        if (enabled) {
          setIsDragOver(false);
          props.setDraggedIndex(null);
        }
      }}
      onDragOver={(event) => {
        if (enabled && event.dataTransfer.types.indexOf(dataKey) !== -1) {
          event.dataTransfer.dropEffect = 'move';
          event.preventDefault();
          const after = props.shouldDropAfter?.(event, index, draggedIndex ?? index);
          if (after !== undefined && after !== isDragAfter) {
            setIsDragAfter(after);
          }
        }
      }}
      onDragEnter={(event) => {
        if (enabled && event.dataTransfer.types.indexOf(dataKey) !== -1) {
          event.preventDefault();
          setIsDragOver(true);
        }
      }}
      onDragLeave={(event) => {
        if (enabled && element.current && !element.current.contains(event.relatedTarget as Node)) {
          setIsDragOver(false);
          setIsDragAfter(null);
        }
      }}
      onDrop={(event) => {
        if (enabled) {
          event.preventDefault();
          const target = index;
          const source = parseInt(event.dataTransfer.getData(dataKey), 10);

          setIsDragOver(false);

          if (isNaN(source) || source === target) {
            return;
          }

          props.setDraggedIndex(null);
          props.onReorder(source, target, isDragAfter ?? false);
          setIsDragAfter(null);
        }
      }}>
      {props.children}
    </div>
  );
};

export const SortableItemHorizontal = (props: SortableItemProps) => {
  const shouldDropAfter = useCallback((event: DragEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const pos = (event.clientX - rect.left) / rect.width;
    return pos > 0.5;
  }, []);

  return (
    <SortableItem
      {...props}
      shouldDropAfter={shouldDropAfter}
      classNames={{
        ...props.classNames,
        dragBefore: classNames(styles.dragBeforeHorizontal, props.classNames?.dragBefore),
        dragAfter: classNames(styles.dragAfterHorizontal, props.classNames?.dragAfter),
      }}
    />
  );
};

export const SortableItemVertical = (props: SortableItemProps) => {
  const shouldDropAfter = useCallback((event: DragEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const pos = (event.clientY - rect.top) / rect.height;
    return pos > 0.5;
  }, []);

  return (
    <SortableItem
      {...props}
      shouldDropAfter={shouldDropAfter}
      classNames={{
        dragBefore: classNames(styles.dragBeforeVertical, props.classNames?.dragBefore),
        dragAfter: classNames(styles.dragAfterVertical, props.classNames?.dragAfter),
      }}
    />
  );
};

/**
 * A simpler version of SortableItem that does not consider geometry when determining
 * whether to drop before or after the item, only the order of items itself.
 */
export const SortableItemSimple = (props: SortableItemProps) => {
  const shouldDropAfter = useCallback((_: DragEvent, index: number, draggedIndex: number) => {
    return index > draggedIndex;
  }, []);

  return (
    <SortableItem
      {...props}
      shouldDropAfter={shouldDropAfter}
      classNames={{
        dragBefore: classNames(styles.dragBeforeVertical, props.classNames?.dragBefore),
        dragAfter: classNames(styles.dragAfterVertical, props.classNames?.dragAfter),
      }}
    />
  );
};
